import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import {
	addDoc,
	collection,
	connectFirestoreEmulator,
	deleteDoc,
	doc,
	DocumentData,
	getDoc,
	getDocs,
	getFirestore,
	query,
	QueryConstraint,
	setDoc,
	WithFieldValue,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { devError, devLog } from '../util/devLog';

const firebaseConfig = {
	apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
	authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
	projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
	storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
	appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'europe-west3');
export const storage = getStorage(app);

if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
	connectAuthEmulator(auth, 'http://127.0.0.1:9099');
	connectFirestoreEmulator(db, '127.0.0.1', 8080);
	connectFunctionsEmulator(functions, '127.0.0.1', 5001);
	connectStorageEmulator(storage, '127.0.0.1', 9199);

	fetch('http://127.0.0.1:8080/')
		.then(() => devLog('Firebase Emulators connected'))
		.catch(() => {
			const showBanner = () => {
				const banner = document.createElement('div');
				banner.id = 'emulator-warning';
				banner.style.cssText =
					'position:fixed;top:0;left:0;right:0;z-index:9999;background:#dc2626;color:white;text-align:center;padding:8px;font-size:14px;font-family:sans-serif;';
				banner.textContent = 'Firebase Emulators not reachable — run "firebase emulators:start" in functions_neutr/';
				document.body.prepend(banner);
			};
			if (document.body) {
				showBanner();
			} else {
				document.addEventListener('DOMContentLoaded', showBanner);
			}
		});
}

const devDelay = () => {
	const ms = Number(import.meta.env.VITE_DEV_DELAY);
	return ms > 0 ? new Promise((r) => setTimeout(r, ms)) : undefined;
};

export const getCollection = async <T>(table: string): Promise<T[]> => {
	return getCollectionWithQuery<T>(table);
};

export const getCollectionWithQuery = async <T>(table: string, ...queryConstraints: QueryConstraint[]): Promise<T[]> => {
	await devDelay();
	const querySnapshot = await getDocs(query(collection(db, table), ...queryConstraints));
	return querySnapshot.docs.map((doc) => ({
		...doc.data(),
		id: doc.id,
	})) as T[];
};

export const getReadOnlyCollectionWithQuery = async <T>(table: string, ...queryConstraints: QueryConstraint[]): Promise<T[]> => {
	try {
		await devDelay();
		const querySnapshot = await getDocs(query(collection(db, table), ...queryConstraints));
		return querySnapshot.docs.map((doc) => ({
			...doc.data(),
			id: doc.id,
		})) as T[];
	} catch (error) {
		devError('Error fetching read-only collection:', error);
		throw error;
	}
};

export const getDocument = async <T>(id: string, table: string): Promise<T> => {
	await devDelay();
	const foundDoc = await getDoc(doc(db, table, id));
	if (foundDoc.exists()) {
		return {
			...foundDoc.data(),
			id: id,
		} as T;
	}
	throw new Error('Required');
};

export const createDocument = async (data: WithFieldValue<DocumentData>, table: string) => {
	return await addDoc(collection(db, table), { ...data, creationDate: new Date() });
};

export const deleteDocument = async (id: string, table: string) => {
	await deleteDoc(doc(db, table, id));
	return true;
};

export const updateDocument = async (id: string, data: WithFieldValue<DocumentData>, table: string) => {
	await setDoc(doc(db, table, id), data, { merge: true });
	return true;
};

interface CloudFunctionResponse<T = unknown> {
	status: string;
	data?: T;
	error?: string;
	message?: string;
}

function extractErrorMessage(response: CloudFunctionResponse): string {
	return response.error || response.message || 'Unknown error';
}

export const createDocumentWithFunction = async (functionName: string, data: Record<string, unknown>) => {
	const createFunctionCall = httpsCallable<Record<string, unknown>, CloudFunctionResponse<{ uid: string }>>(functions, functionName);
	const result = await createFunctionCall(data);
	const resultData = result.data;

	if (resultData.status === 'ok' && resultData.data) {
		return resultData.data.uid;
	}

	throw new Error(extractErrorMessage(resultData));
};

export const doOperationWithFunction = async <T = unknown>(functionName: string, data: Record<string, unknown>): Promise<T> => {
	const createFunctionCall = httpsCallable<Record<string, unknown>, CloudFunctionResponse<T>>(functions, functionName);
	const result = await createFunctionCall(data);
	const resultData = result.data;

	if (resultData.status === 'ok') {
		return resultData.data as T;
	}

	throw new Error(extractErrorMessage(resultData));
};

export default app;
